import { Group as HwGroup, User as HwUser } from 'src/app/handweave-api';
import { Directory as HwDirectory } from 'src/app/directory-api';
import { GroupSummary as HkGroupSummary, Group as HkGroup, Contact as HkContact, PersonName, GroupTreeNode as HkNode, PagedApiResponse } from 'src/app/hickory-api';
import { toInteger, toString } from '../../model/utility';
import { BaseDirectoryService } from './base-directory.service';
import { HickoryDirectoryService } from '../hickory/hickory-directory.service';

export class Directory {
  id: string;
  type: DirectoryType;
  name: string;
  description: string;
  browsable: boolean;
  classificationType: string;
  source: HwDirectory = null;

  totalRecipientCount: number = -1;

  static readonly HK_INTERNAL_DIRECTORY_ID = '00000000-0000-0000-0000-000000000000';
  static readonly HK_PUBLIC_DIRECTORY_ID =   '11111111-1111-1111-1111-111111111111';

  static isLegacy(directoryId: string): boolean {
    return directoryId === this.HK_INTERNAL_DIRECTORY_ID || directoryId === this.HK_PUBLIC_DIRECTORY_ID;
  }

  static fromApi(dir: HwDirectory): Directory {
    const converted = new Directory();
    converted.id = dir.id;
    converted.source = dir;
    converted.type = dir.directoryType;
    converted.name = dir.name;
    converted.classificationType = dir.classification?.find(_ => true);

    return converted;
  }
}

export class PseudoDirectory extends Directory {
  isPseudo: boolean = true;

  static fromClassification(classification: string): PseudoDirectory {
    const dir = new PseudoDirectory();
    dir.id = this.psuedoId(classification, true);
    dir.type = DirectoryType.Hickory;
    dir.name = classification + ' Directory';
    dir.browsable = true;
    dir.classificationType = classification;
    return dir;
  }

  static single(): PseudoDirectory {
    const dir = new PseudoDirectory();
    dir.id = this.psuedoId();
    dir.type = DirectoryType.Hickory;
    dir.name = 'Contacts and Groups';
    dir.browsable = true;
    return dir;
  }

  static list(): PseudoDirectory[] {
    return [
      this.fromClassification('Internal'),
      this.fromClassification('Public'),
    ];
  }

  static psuedoId(classification?: string, splitDirectory?: boolean) {
    return (classification === 'Public' && splitDirectory === true) ? this.HK_PUBLIC_DIRECTORY_ID : this.HK_INTERNAL_DIRECTORY_ID;
  }

  static getClassification(directoryId: string, splitDirectory: boolean): string {
    if (!splitDirectory) return null;
    if (directoryId === this.HK_INTERNAL_DIRECTORY_ID) return 'Internal';
    if (directoryId === this.HK_PUBLIC_DIRECTORY_ID) return 'Public';
    return null;
  }
}

export enum DirectoryType {
  Handweave = 'Handweave',
  Hickory = 'Hickory',
  Unsupported = 'Unsupported',
}

export class DirectoryGroup {
  id?: string;
  hickoryId?: number;
  directoryId?: string;
  externalId?: string;
  parentId?: string;
  members?: number;
  children?: number;
  name: string;
  isLinked: boolean = false;
  isMissing: boolean = false;
  type: DirectoryType;
  classification: string;
  source?: any;
  
  private _root: boolean = false;
  get root(): boolean { return this._root; }
  set root(value: boolean) {
    this._root = value;
    if (value === true) this.parentId = undefined;
  }

  get hickoryParentId(): number {
    return toInteger(this.parentId);
  }

  toHkGroupSummary(): HkGroupSummary {
    return {
      id: this.hickoryId,
      externalId: this.externalId,
      isLink: this.isLinked,
      name: this.name,
      classification: this.classification,
      parentId: this.hickoryParentId,
    }
  }

  static fromMissingId(id: string, directoryId: string, service: BaseDirectoryService): DirectoryGroup {
    const converted = new DirectoryGroup();
    converted.id = id;
    converted.directoryId = directoryId;
    converted.name = 'Unknown Group';
    converted.isMissing = true;
    converted.type = service?.directoryType
    converted.parentId = '-1';
    if (service?.directoryType === DirectoryType.Hickory) {
      const hk = service as HickoryDirectoryService;
      converted.classification = hk.classification;
      converted.hickoryId = toInteger(id);
    } else if (service?.directoryType === DirectoryType.Handweave) {
      // no-op for now
    }
    return converted;
  }

  static fromHickory(item: HkGroupSummary, splitDirectory: boolean, parentId?: number | string): DirectoryGroup {
    const converted = new DirectoryGroup();
    converted.directoryId = PseudoDirectory.psuedoId(item.classification, splitDirectory);
    converted.type = DirectoryType.Hickory;
    converted.classification = item.classification;
    converted.hickoryId = item.id;
    converted.id = toString(item.id);
    converted.name = item.name;
    converted.isLinked = item.isLink ?? false;
    converted.source = item;
    converted.externalId = item.externalId;
    converted.parentId = toString(item['parentId']) ?? toString(parentId) ?? undefined;

    return converted;
  }

  static fromHickoryFull(item: HkGroup, splitDirectory: boolean, parentId?: number | string): DirectoryGroup {
    const converted = this.fromHickory(item, splitDirectory, parentId);
    converted.externalId = converted.externalId ?? item.importedid;
    converted.children = item.subgroups?.totalItemCount ?? 0;
    converted.members = item.contacts?.totalItemCount;
    if (!!converted.children && !converted.members) converted.members = null;
    return converted;
  }

  static fromHickoryNode(item: HkNode, splitDirectory: boolean, parentId?: number | string): DirectoryGroup {
    const converted = this.fromHickory(item, splitDirectory, parentId);
    converted.children = item.childGroupCount;
    converted.members = item.memberCount;
    if (!!converted.children && !converted.members) converted.members = null;
    return converted;
  }

  static fromHandweave(item: HwGroup): DirectoryGroup {
    const converted = new DirectoryGroup();
    converted.directoryId = item.directoryId;
    converted.type = DirectoryType.Handweave;
    converted.id = item.id;
    converted.name = item.name;
    // converted.isLinked = item.isLink ?? false; // TODO: handweave linked groups?
    converted.source = item;
    converted.children = item.children?.length // ?? 0;
    converted.members = item.members?.length //?? 0;
    converted.parentId = item.parentId;
    if (!!converted.children && !converted.members) converted.members = null;

    return converted;
  }
}

export interface PagedDirectoryGroups {
  items?: DirectoryGroup[];
  page?: number;
  pageSize?: number;
  totalItemCount?: number;
}

export class DirectoryEmail {
  value: string;
  primary?: boolean = false;
  type?: string;
}

export class DirectoryPhone {
  value: string;
  primary?: boolean = false;
  sms?: boolean = false;
  type?: string;
}

export class DirectoryUser {
  id: string;
  hickoryId?: number;
  directoryId?: string;
  externalId?: string;
  userName?: string;
  name?: PersonName;
  displayName?: string;
  lastFirstName?: string;
  emails?: DirectoryEmail[];
  phonesNumbers?: DirectoryPhone[];
  isMissing: boolean = false;
  type: DirectoryType;
  classification: string;
  source?: any;
  acknowledgment?: boolean;

  static fromMissingId(id: string, directoryId: string, service: BaseDirectoryService): DirectoryUser {
    const converted = new DirectoryUser();
    converted.id = id;
    converted.directoryId = directoryId;
    converted.displayName = 'Unknown User';
    converted.isMissing = true;
    converted.type = service?.directoryType
    if (service?.directoryType === DirectoryType.Hickory) {
      const hk = service as HickoryDirectoryService;
      converted.classification = hk.classification;
      converted.hickoryId = toInteger(id);
    } else if (service?.directoryType === DirectoryType.Handweave) {
      // no-op for now
    }
    return converted;
  }

  static fromHickory(item: HkContact, splitDirectory: boolean): DirectoryUser {
    const converted = new DirectoryUser();
    converted.directoryId = PseudoDirectory.psuedoId(item.type, splitDirectory);
    converted.type = DirectoryType.Hickory;
    converted.classification = item.type;
    converted.hickoryId = item.id;
    converted.id = item.id + '';
    converted.source = item;
    converted.externalId = item.externalId;
    converted.name = { familyName: item.lastName, givenName: item.firstName };
    converted.displayName = item.firstName + ' ' + item.lastName;
    converted.lastFirstName = item.lastName + ', ' + item.firstName;

    converted.emails = item.emails?.map(e => { return { value: e.address, }; });
    converted.phonesNumbers = item.phones?.map(p => { return { value: p.number, type: p.phoneType?.toString(), }; });

    return converted;
  }

  static fromHandweave(item: HwUser): DirectoryUser {
    const converted = new DirectoryUser();
    converted.id = item.id;
    converted.type = DirectoryType.Handweave;
    converted.directoryId = item.directoryId;
    converted.source = item;
    converted.name = { familyName: item.lastName, givenName: item.firstName };
    converted.displayName = item.displayName ?? (item.firstName + ' ' + item.lastName);
    converted.lastFirstName = item.lastName + ', ' + item.firstName;

    converted.phonesNumbers = item.phoneNumbers?.map(p => { return { value: p.value, type: p.type, }; });
    converted.emails = item.emails?.map(e => { return { value: e.value, }; });
    // check for an email in the attributes dictionary
    if (!converted.emails?.length) {
      const fallback = item.attributes ? item.attributes['email'] : null;
      if (fallback?.length) {
        converted.emails = [ { value: fallback, }];
      }
    }

    return converted;
  }
}

export interface PagedDirectoryUsers extends PagedApiResponse { 
  items?: DirectoryUser[];
}
